import styled from '@emotion/styled'
import type { FC } from 'react'

import { useTheme } from '@emotion/react'

import sanitizeHtml from 'sanitize-html'

import Paper from '@segment/matcha/components/Paper'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import AccordionBase, * as Accordion from '@segment/matcha/recipes/Accordion'

import { useActiveState } from '@segment/utils/useActiveState'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import type { AccordionDataProps } from './data'

interface AccordionsProps {
  data: AccordionDataProps[]
}

const Accordions: FC<AccordionsProps> = ({ data }) => {
  const theme = useTheme()
  const pageConfig = usePageConfig()
  const items = new Array(data.length).fill(false)
  const { activeItems, toggle } = useActiveState(items, {
    allowMultipleActive: true,
  })

  return (
    <StyledPaper background={theme.palette.neutrals.white[100]} border={`1px solid ${theme.palette.twilio.gray[20]}`}>
      {activeItems.map((active, index) => (
        <AccordionBase key={`item-${index}`}>
          <Accordion.Header onClick={() => toggle(index)}>
            <Accordion.TitleWrapper>
              <Accordion.Title opened={active}>{data[index].title}</Accordion.Title>
            </Accordion.TitleWrapper>
            <Accordion.Icon opened={active} />
          </Accordion.Header>
          <Accordion.Body>
            {active && (
              <StyledAccordionContent>
                <Accordion.Text tag="div">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(data[index].description, { allowedTags: [`a`, `ul`, `li`] }),
                    }}
                  />
                </Accordion.Text>

                <ButtonWrapper>
                  <ButtonText
                    href={
                      data[index].type === `docs`
                        ? getAbsoluteUrl(`${data[index].link}`, pageConfig)
                        : getAbsoluteUrl(`/catalog/integrations/${data[index].type}${data[index].link}/`, pageConfig)
                    }
                    iconEnd={ChevronRightIcon}
                  >
                    See how it works
                  </ButtonText>
                </ButtonWrapper>
              </StyledAccordionContent>
            )}
          </Accordion.Body>
        </AccordionBase>
      ))}
    </StyledPaper>
  )
}

const StyledPaper = styled(Paper)`
  margin-top: 40px;
  padding: 20px 40px 40px;
`

const StyledAccordionContent = styled(Accordion.ContentWrapper)`
  overflow: visible;

  ul {
    margin-top: 20px;
  }
`

const ButtonWrapper = styled.div`
  margin-top: 24px;
`

export default Accordions
